'use client';

import type { SyntheticEvent } from 'react';
import { SafeRemoteImage } from '@/components/safe-remote-image';
import { cn } from '@/lib/utils';
import { getProductMediaType, type ProductMediaType } from '@/lib/productMedia';

type ProductMediaProps = {
  src?: string | null;
  alt: string;
  className?: string;
  sizes?: string;
  priority?: boolean;
  fill?: boolean;
  width?: number;
  height?: number;
  mediaType?: ProductMediaType | null;
  controls?: boolean;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  playsInline?: boolean;
  onError?: (event: SyntheticEvent<HTMLImageElement | HTMLVideoElement>) => void;
};

export function ProductMedia({
  src,
  alt,
  className,
  sizes,
  priority,
  fill = false,
  width = 80,
  height = 80,
  mediaType,
  controls = false,
  autoPlay = false,
  muted = true,
  loop = false,
  playsInline = true,
  onError,
}: ProductMediaProps) {
  const trimmed = src?.trim();
  if (!trimmed) return null;

  const resolvedType = mediaType ?? getProductMediaType(trimmed);

  if (resolvedType === 'video') {
    return (
      <video
        key={trimmed}
        src={trimmed}
        aria-label={alt}
        className={cn(fill && 'absolute inset-0 h-full w-full object-cover', className)}
        controls={controls}
        autoPlay={autoPlay}
        muted={muted}
        loop={loop}
        playsInline={playsInline}
        preload={priority ? 'auto' : 'metadata'}
        onError={onError}
      />
    );
  }

  if (fill) {
    return (
      <SafeRemoteImage
        src={trimmed}
        alt={alt}
        fill
        sizes={sizes}
        className={className}
        priority={priority}
        onError={onError}
      />
    );
  }

  return (
    <SafeRemoteImage
      src={trimmed}
      alt={alt}
      width={width}
      height={height}
      sizes={sizes}
      className={className}
      priority={priority}
      onError={onError}
    />
  );
}
